import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { SpinnerGap, ArrowLeft, CaretUp, CaretDown, Minus, ArrowUp, ArrowDown } from '@phosphor-icons/react';
import { PersonLink } from '@/components/PersonLink';

const MONTHS = ['', 'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];

export default function ProjectionPage() {
  const navigate = useNavigate();
  const { activeId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [rawData, setRawData] = useState([]);
  const [compFat, setCompFat] = useState(null);
  const [sortKey, setSortKey] = useState('proj');
  const [sortDir, setSortDir] = useState('desc');

  const fmt = (v) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v || 0);
  const fmtShort = (v) => {
    if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}k€`;
    return `${v.toFixed(0)}€`;
  };

  const currentMonth = new Date().getMonth() + 1;
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    setLoading(true);
    api.post('/dashboard/init').then(r => {
      const d = r.data || {};
      setRawData(Array.isArray(d.projecaoIndividual) ? d.projecaoIndividual : []);
      setCompFat(d.comparativoFaturacao || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [activeId]);

  const { consultants, networkProj, networkGrowth, networkYTD, networkLYTotal } = useMemo(() => {
    // Network totals
    let netProj = 0, netGrowth = 0, netYTD = 0, netLY = 0;
    if (compFat) {
      const cy = compFat.currentYear || [];
      const ly = compFat.lastYear || [];
      netYTD = cy.reduce((s, c) => s + (c.Valor || 0), 0);
      netLY = ly.reduce((s, l) => s + (l.Valor || 0), 0);
      const lySame = ly.filter(l => l.Mes <= currentMonth).reduce((s, l) => s + (l.Valor || 0), 0);
      const factor = lySame > 0 ? netYTD / lySame : 1;
      netGrowth = lySame > 0 ? (factor - 1) * 100 : 0;
      netProj = netYTD + ly.filter(l => l.Mes > currentMonth).reduce((s, l) => s + ((l.Valor || 0) * factor), 0);
    }

    // Per-consultant
    const byC = {};
    for (const r of rawData) {
      const cid = r.ID_Cliente;
      if (!byC[cid]) byC[cid] = { id: cid, name: r.NomeCompleto, ly: {}, cy: {} };
      if (r.Ano === currentYear - 1) byC[cid].ly[r.Mes] = r.Valor || 0;
      else byC[cid].cy[r.Mes] = r.Valor || 0;
    }

    const list = Object.values(byC).map(d => {
      const lyMonths = {};
      const cyMonths = {};
      let lyTotal = 0, lySame = 0, ytd = 0;
      for (let m = 1; m <= 12; m++) {
        lyMonths[m] = d.ly[m] || 0;
        cyMonths[m] = d.cy[m] || 0;
        lyTotal += lyMonths[m];
        if (m <= currentMonth) { lySame += lyMonths[m]; ytd += cyMonths[m]; }
      }
      let factor;
      if (lySame > 0) factor = ytd / lySame;
      else if (ytd > 0) factor = 2.0;
      else factor = 0;
      const remaining = Array.from({ length: 12 - currentMonth }, (_, i) => lyMonths[currentMonth + 1 + i] * factor).reduce((a, b) => a + b, 0);
      const proj = ytd + remaining;
      const growth = lySame > 0 ? (factor - 1) * 100 : (ytd > 0 ? 100 : 0);

      return {
        id: d.id, name: d.name,
        lyTotal, lySame, ytd, factor, proj, growth,
        lyMonths, cyMonths,
      };
    });

    return {
      consultants: list,
      networkProj: netProj, networkGrowth: netGrowth,
      networkYTD: netYTD, networkLYTotal: netLY,
    };
  }, [rawData, compFat, currentMonth, currentYear]);

  const sorted = useMemo(() => {
    const keys = {
      name: (a) => a.name.toLowerCase(),
      lyTotal: (a) => a.lyTotal,
      ytd: (a) => a.ytd,
      growth: (a) => a.growth,
      proj: (a) => a.proj,
    };
    const fn = keys[sortKey] || keys.proj;
    return [...consultants].sort((a, b) => {
      const va = fn(a), vb = fn(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [consultants, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const SortHeader = ({ k, children, align = 'left' }) => (
    <th onClick={() => toggleSort(k)}
      className={`text-${align} text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] px-3 py-2.5 cursor-pointer hover:text-[#111827] select-none whitespace-nowrap`}>
      <span className="inline-flex items-center gap-0.5">
        {children}
        {sortKey === k && (sortDir === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />)}
      </span>
    </th>
  );

  // Totals
  const totalProj = consultants.reduce((s, c) => s + c.proj, 0);
  const totalYTD = consultants.reduce((s, c) => s + c.ytd, 0);
  const totalLY = consultants.reduce((s, c) => s + c.lyTotal, 0);

  if (loading) return (
    <div className="flex justify-center items-center py-20">
      <SpinnerGap size={28} className="animate-spin text-[#0F1829]" />
    </div>
  );

  return (
    <div data-testid="projection-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <button data-testid="back-btn" onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-[#0F1829] hover:underline mb-4">
        <ArrowLeft size={16} /> Voltar ao Dashboard
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-[#111827]" style={{ fontFamily: "'Fraunces', sans-serif" }}>
          Projeção por Consultor — Dez {currentYear}
        </h1>
        <p className="text-sm text-[#6B7280] mt-1">
          Factor de crescimento individual de cada consultor aplicado à faturação pessoal do ano anterior
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Proj. Individual (soma)</p>
          <p data-testid="sum-proj-indiv" className="text-xl font-bold text-[#978D78]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(totalProj)}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Proj. Rede (bloco)</p>
          <p data-testid="sum-proj-rede" className="text-xl font-bold text-[#E65100]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{fmt(networkProj)}</p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Diferença</p>
          <p className="text-xl font-bold text-[#111827]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            {fmt(totalProj - networkProj)}
            <span className="text-xs font-normal text-[#6B7280] ml-1">
              ({networkProj > 0 ? ((totalProj / networkProj - 1) * 100).toFixed(1) : 0}%)
            </span>
          </p>
        </div>
        <div className="bg-white border border-[#E5E7EB] rounded-lg p-4">
          <p className="text-[10px] uppercase tracking-widest text-[#6B7280] mb-1">Consultores com faturação</p>
          <p className="text-xl font-bold text-[#111827]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{consultants.length}</p>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
                <SortHeader k="name">Consultor</SortHeader>
                <SortHeader k="lyTotal" align="right">{currentYear - 1} Total</SortHeader>
                <SortHeader k="ytd" align="right">YTD {currentYear}</SortHeader>
                <SortHeader k="growth" align="right">Crescimento</SortHeader>
                <SortHeader k="proj" align="right">Projeção Dez</SortHeader>
                <th className="text-right text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] px-3 py-2.5">Peso</th>
                <th className="text-center text-[10px] uppercase tracking-[0.15em] font-medium text-[#6B7280] px-3 py-2.5">Mensal {currentYear}</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(c => {
                const weight = totalProj > 0 ? (c.proj / totalProj * 100) : 0;
                const isGrowing = c.growth > 5;
                const isDecline = c.growth < -5;
                return (
                  <tr key={c.id} data-testid={`row-${c.id}`}
                    className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB] transition-colors">
                    <td className="px-3 py-2.5 text-sm font-medium text-[#111827] whitespace-nowrap">
                      <PersonLink id={c.id} name={c.name} />
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right font-mono text-[#6B7280]">{fmt(c.lyTotal)}</td>
                    <td className="px-3 py-2.5 text-sm text-right font-mono text-[#111827]">{fmt(c.ytd)}</td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      {(() => {
                        let badgeCls = 'bg-gray-50 text-gray-500';
                        if (isGrowing) badgeCls = 'bg-green-50 text-green-700';
                        else if (isDecline) badgeCls = 'bg-red-50 text-red-600';
                        let icon = <Minus size={10} />;
                        if (isGrowing) icon = <ArrowUp size={10} weight="bold" />;
                        else if (isDecline) icon = <ArrowDown size={10} weight="bold" />;
                        return (
                          <span className={`inline-flex items-center gap-0.5 text-xs font-semibold px-1.5 py-0.5 rounded ${badgeCls}`}>
                            {icon}
                            {c.factor > 0 ? `${c.factor.toFixed(2)}x` : '—'}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-2.5 text-sm text-right font-mono font-semibold text-[#111827]">{fmt(c.proj)}</td>
                    <td className="px-3 py-2.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <div className="w-16 h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                          <div className="h-full rounded-full bg-[#978D78]" style={{ width: `${Math.min(weight, 100)}%` }} />
                        </div>
                        <span className="text-[10px] text-[#6B7280] w-8 text-right">{weight.toFixed(1)}%</span>
                      </div>
                    </td>
                    <td className="px-3 py-1.5">
                      <div className="flex gap-px justify-center">
                        {Array.from({ length: 12 }, (_, i) => {
                          const m = i + 1;
                          const v = c.cyMonths[m] || 0;
                          const max = Math.max(...Object.values(c.lyMonths), ...Object.values(c.cyMonths), 1);
                          const h = Math.max(2, (v / max) * 20);
                          const isProj = m > currentMonth;
                          let barCls = 'bg-[#E5E7EB]';
                          if (!isProj && m <= currentMonth && v > 0) {
                            barCls = 'bg-[#0F1829]';
                          }
                          return (
                            <div key={i} title={`${MONTHS[m]} ${currentYear}: ${isProj ? 'proj.' : fmt(v)}`}
                              className={`w-1.5 rounded-t ${barCls}`}
                              style={{ height: isProj ? 2 : h }} />
                          );
                        })}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-[#F9FAFB] border-t-2 border-[#E5E7EB] font-semibold">
                <td className="px-3 py-3 text-sm text-[#111827]">Total ({consultants.length} consultores)</td>
                <td className="px-3 py-3 text-sm text-right font-mono">{fmt(totalLY)}</td>
                <td className="px-3 py-3 text-sm text-right font-mono">{fmt(totalYTD)}</td>
                <td className="px-3 py-3 text-right">
                  <span className="text-xs font-semibold text-[#6B7280]">—</span>
                </td>
                <td className="px-3 py-3 text-sm text-right font-mono font-bold text-[#978D78]">{fmt(totalProj)}</td>
                <td className="px-3 py-3 text-right text-[10px] text-[#6B7280]">100%</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
}
